import { getTweetDisplayText, renderableTweetMediaUrl } from 'lib/twitterText'
import {
    bucketedMediaHeight,
    bucketedMediaGridLayout,
    RESERVED_URL_CARD_IMAGE_HEIGHT,
} from 'lib/mediaBuckets'

// Mirrors LinkPreview.twitterRegexp — the dispatch that decides TwitterUrl (our local card) vs the
// generic LinkPreview card. The estimator must use the SAME test so tweets get the tweet estimate
// and everything else gets the generic reserved-card estimate.
export const twitterRegexp = /^https?:\/\/(www\.)?(x|twitter)\.com\/[^/]+\/status\/([0-9]+)/

// Chrome constants derived from twitter-url.module.scss. Unlike a generic link card, a tweet card
// has NO single constant height: its text grows with content length. So we reserve the constant
// chrome + a content-derived text block + a dims-derived media block, matching the render exactly.
const TWEET_OUTER_CHROME_PX = 22 // .twitterUrlComp: padding 10+10 + border 1+1
const TWEET_HEADER_PX = 48 // .userHeader: avatar is 48 tall and defines the row
const TWEET_CONTENT_VMARGIN_PX = 20 // .contentContainer: margin-top 10 + margin-bottom 10
export const TWEET_TEXT_LINE_PX = 20 // .contentContainer: line-height 20
const TWEET_DATE_PX = 25 // .dateTimeContainer: margin-top 5 + ~20 line
const TWEET_MEDIA_VMARGIN_PX = 10 // .mediaContainer: margin-bottom 10
const TWEET_MEDIA_GRID_GAP_PX = 2 // media-grid.module.scss .mediaGrid gap
const TWEET_TEXT_CHARS_PER_LINE = 72 // matches the answer-text estimators' chars/line convention
// Horizontal chrome between the estimator's row width and the tweet text: .urlAnswerContainer
// margin-left 15 + padding-right 15, then .twitterUrlComp padding 10+10 + border 1+1.
const TWEET_TEXT_HORIZONTAL_CHROME_PX = 52
// .contentContainer renders 14px system font (the answer-text estimators use 7.25 at 15px).
const TWEET_AVERAGE_CHAR_WIDTH_PX = 7
const TWEET_MIN_WRAP_CHARS_PER_LINE = 34 // matches the answer-text estimators' narrow-panel floor
// Collapsed tweets truncate to 280 chars, but the card keeps the post's line breaks, so 280 chars
// of short lines can run well past the ~9 wrapped lines of prose. Live payloads top out around
// 13 lines at narrow widths; TwitterUrl pins the rendered text to this same window (internal
// scroll) so the rare taller post stays readable inside the frozen row reserve.
export const TWEET_MAX_TEXT_LINES = 20
// The "more" toggle swaps in the full note_tweet/decoded text with no truncation; the cap only
// guards against degenerate payloads, so it must clear a long note tweet at mobile wrap widths.
export const TWEET_EXPANDED_MAX_TEXT_LINES = 120

// Nested quote-tweet chrome: borders 1+1 + .twitterUrlEmbedComp padding-top 10 + header row 20
// (embed avatar). Its text block (.contentContainer margins + lines) and media terms are
// projected from the quoted tweet itself so the pinned render window can show the whole card
// without changing height.
export const TWEET_QUOTE_EMBED_CHROME_PX = 32
// Quote text sits inside both the host tweet and nested quote card. This is the row-width chrome
// before the quoted text: top-level 52 + nested borders/padding 22.
const TWEET_QUOTE_TEXT_HORIZONTAL_CHROME_PX = 74
// Loading quote skeleton: nested chrome 12 + 20px header + 54px text skeleton + 10px media
// margin + the embed skeleton's 180px maximum media shell.
export const TWEET_QUOTE_EMBED_SKELETON_HEIGHT_PX = 276
// The website embed card is already a constant in CSS: .webEmbedComp height 130 (border-box).
export const TWEET_WEBSITE_EMBED_HEIGHT_PX = 130

// Constant chrome for a rendered (non-skeleton) tweet card, before text/media/optional date.
const TWEET_BASE_CHROME_PX = TWEET_OUTER_CHROME_PX + TWEET_HEADER_PX

// Fixed-shell skeleton (TwitterPostPlaceholder) height under the flag: outer 22 + header 48 +
// skeletonContent (margin-top 14 + 3×12 lines + 2×8 gaps = 66) + mediaShell (margin-top 14 + fixed
// 160) + footer (margin-top 12 + 12 = 24). The skeleton's media shell is pinned to a constant under
// the flag (see TwitterPostPlaceholder), so this height is width-independent.
export const TWEET_SKELETON_HEIGHT_PX =
    TWEET_OUTER_CHROME_PX + TWEET_HEADER_PX + 66 + (14 + RESERVED_URL_CARD_IMAGE_HEIGHT) + 24

export type RenderableTweetMediaItem = {
    type: 'video' | 'image' | 'pending'
    url: string
    thumbnailUrl?: string
    width?: number
    height?: number
    metadata: any
}

function positiveNumber(value: unknown): number | null {
    const n = Number(value)
    return Number.isFinite(n) && n > 0 ? n : null
}

// Does this url render via our local TwitterUrl card (as opposed to a generic LinkPreview)?
export function isTweetUrl(url: any): boolean {
    if (!url) return false
    if (url.service_name === 'twitter') return true
    if (url.tweet_id) return true
    return typeof url.address === 'string' && twitterRegexp.test(url.address)
}

const renderableMediaUrl = (value: unknown): value is string => renderableTweetMediaUrl(value)

// Keep media presence, rendering, and height projection on one predicate (shared with the text
// layer, which drops a media link only when the media renders). Some historical X payloads
// contain blank entries in image_urls/video_urls; array length alone reserves a 160px strip even
// though MediaGrid has no usable source to render into it.
export function getRenderableTweetMediaItems(url: any): RenderableTweetMediaItem[] {
    const videos = Array.isArray(url?.video_urls)
        ? url.video_urls.flatMap((videoUrl: unknown, index: number) => {
              if (!renderableMediaUrl(videoUrl)) return []
              const metadata = url?.video_metadata?.[index] || {}
              const thumbnailUrl = url?.video_thumbnail_urls?.[index]
              return [
                  {
                      type: 'video' as const,
                      url: videoUrl,
                      thumbnailUrl: renderableMediaUrl(thumbnailUrl) ? thumbnailUrl : undefined,
                      width: positiveNumber(metadata.width) || undefined,
                      height: positiveNumber(metadata.height) || undefined,
                      metadata,
                  },
              ]
          })
        : []
    const images = Array.isArray(url?.image_urls)
        ? url.image_urls.flatMap((imageUrl: unknown, index: number) => {
              if (!renderableMediaUrl(imageUrl)) return []
              const metadata = url?.image_metadata?.[index] || {}
              return [
                  {
                      type: 'image' as const,
                      url: imageUrl,
                      width: positiveNumber(metadata.width) || undefined,
                      height: positiveNumber(metadata.height) || undefined,
                      metadata,
                  },
              ]
          })
        : []

    return [...videos, ...images].slice(0, 4)
}

// While the server downloads a freshly synced tweet's media (url.tweet_media_pending), reserve one
// tile per photo/video at the dims X reported, in the same video-then-image order the real media
// renders in, so the card keeps its height when the media arrives.
function getPendingTweetMediaItems(url: any): RenderableTweetMediaItem[] {
    if (url?.tweet_media_pending !== true) return []
    const metadata = [
        ...(Array.isArray(url?.video_metadata) ? url.video_metadata : []),
        ...(Array.isArray(url?.image_metadata) ? url.image_metadata : []),
    ]
    return metadata.slice(0, 4).map((meta: any) => ({
        type: 'pending' as const,
        url: '',
        width: positiveNumber(meta?.width) || undefined,
        height: positiveNumber(meta?.height) || undefined,
        metadata: meta || {},
    }))
}

// What the card's MediaGrid renders: the synced media, else the pending media's reserved tiles.
export function getTweetMediaGridItems(url: any): RenderableTweetMediaItem[] {
    const items = getRenderableTweetMediaItems(url)
    return items.length > 0 ? items : getPendingTweetMediaItems(url)
}

// The following three predicates mirror TwitterUrl.jsx's render gate exactly, so the estimate for a
// tweet matches whichever branch the card actually renders (skeleton / terminal / real content).
function tweetHasSyncedData(url: any): boolean {
    return !!url?.service_json?.id && !url?.service_json?.error
}

function tweetIsTerminal(url: any): boolean {
    return url?.service_status === 'twitter_missing'
}

function tweetIsLoading(url: any): boolean {
    const hasReference = !!(
        url?.address ||
        url?.service_id ||
        url?.tweet_id ||
        url?.service_json?.id
    )
    return hasReference && !tweetHasSyncedData(url) && !tweetIsTerminal(url)
}

function resolveTweetWrapCharsPerLine(
    width: unknown,
    horizontalChrome = TWEET_TEXT_HORIZONTAL_CHROME_PX,
): number {
    const w = Number(width)
    if (!Number.isFinite(w) || w <= 0) return TWEET_TEXT_CHARS_PER_LINE

    const textWidth = Math.max(0, w - horizontalChrome)
    return Math.min(
        TWEET_TEXT_CHARS_PER_LINE,
        Math.max(
            TWEET_MIN_WRAP_CHARS_PER_LINE,
            Math.floor(textWidth / TWEET_AVERAGE_CHAR_WIDTH_PX),
        ),
    )
}

// The rendered tweet text keeps the post's line breaks (.selection is white-space: pre-wrap), so
// each paragraph wraps on its own at the panel's real width and a blank line still costs a line.
// The text is the DISPLAY text from getTweetDisplayText — t.co links already swapped for their
// display_url and the media/quote-card links already dropped — so the count matches the glyphs
// on screen. A fixed 72 chars/line under-reserves ~380-450px panels (which wrap at ~40-50 chars).
function estimateTweetTextLines(
    text: string,
    {
        width = 0,
        expanded = false,
        horizontalChrome = TWEET_TEXT_HORIZONTAL_CHROME_PX,
    }: { width?: number; expanded?: boolean; horizontalChrome?: number } = {},
): number {
    const charsPerLine = resolveTweetWrapCharsPerLine(width, horizontalChrome)
    const lines = String(text || '')
        .split(/\r?\n/)
        .reduce(
            (sum, paragraph) =>
                sum + Math.max(1, Math.ceil(paragraph.replace(/\s+$/, '').length / charsPerLine)),
            0,
        )

    const maxLines = expanded ? TWEET_EXPANDED_MAX_TEXT_LINES : TWEET_MAX_TEXT_LINES
    return Math.min(maxLines, Math.max(1, lines))
}

// The text block TwitterUrl renders: .contentContainer (margins 10+10) around the wrapped lines,
// or nothing at all when the display text is empty — a photo-only post whose API text was just
// its media link renders no text block.
function estimateTweetTextBlockHeight(
    text: string,
    options: { width?: number; expanded?: boolean; horizontalChrome?: number } = {},
): number {
    if (!text) return 0
    return TWEET_CONTENT_VMARGIN_PX + estimateTweetTextLines(text, options) * TWEET_TEXT_LINE_PX
}

// The collapsed card renders the truncated text plus its inline " more" toggle on the same
// line, so the toggle's width counts toward the last paragraph's wrap.
function collapsedTweetRenderText(url: any): string {
    const { text, isTruncated } = getTweetDisplayText(url, false)
    return isTruncated ? `${text} more` : text
}

export type TweetEmbedGeometryProjection =
    | {
          kind: 'twitter'
          loading: boolean
          collapsedText: string
          expandedText: string
          mediaTileHeights: number[]
      }
    | { kind: 'website' }
    | null

function projectTweetEmbedGeometry(url: any): TweetEmbedGeometryProjection {
    const embed = url?.embed
    if (!embed || typeof embed !== 'object') return null
    if (embed.service_name === 'twitter') {
        return {
            kind: 'twitter',
            loading: tweetIsLoading(embed),
            collapsedText: collapsedTweetRenderText(embed),
            expandedText: getTweetDisplayText(embed, true).text,
            mediaTileHeights: getTweetMediaGridItems(embed).map(item =>
                tweetMediaBucketHeight(item.metadata),
            ),
        }
    }
    if (embed.service_name === 'website') return { kind: 'website' }
    return null
}

function tweetMediaBucketHeight(meta: any): number {
    const width = positiveNumber(meta?.width)
    const height = positiveNumber(meta?.height)
    return width && height ? bucketedMediaHeight(width, height) : RESERVED_URL_CARD_IMAGE_HEIGHT
}

function estimateTweetGridHeight(tileHeights: number[]): number {
    if (tileHeights.length === 0) return 0
    if (tileHeights.length === 1) return tileHeights[0]

    return bucketedMediaGridLayout(tileHeights, TWEET_MEDIA_GRID_GAP_PX)?.height ?? 0
}

// A card has ONE expansion state: the row's tweet-expanded flag (or the card's local state
// without a provider) opens the host text and the quoted text together, so a quote's "more"
// grows the reserve through the same channel instead of scrolling inside a frozen window.
function estimateTweetEmbedProjectionHeight(
    projection: TweetEmbedGeometryProjection,
    { width = 0, expanded = false }: { width?: number; expanded?: boolean } = {},
): number {
    if (!projection) return 0
    if (projection.kind === 'website') return TWEET_WEBSITE_EMBED_HEIGHT_PX
    if (projection.loading) return TWEET_QUOTE_EMBED_SKELETON_HEIGHT_PX

    return (
        TWEET_QUOTE_EMBED_CHROME_PX +
        estimateTweetTextBlockHeight(
            expanded ? projection.expandedText : projection.collapsedText,
            {
                width,
                expanded,
                horizontalChrome: TWEET_QUOTE_TEXT_HORIZONTAL_CHROME_PX,
            },
        ) +
        estimateTweetGridHeight(projection.mediaTileHeights)
    )
}

// Mirrors TwitterUrl's embed render gate and fixed-height wrapper. The quote window is derived
// from the same wrapped text + media buckets that TwitterUrlEmbed renders, so the nested card is
// fully visible while its host answer keeps deterministic geometry.
export function estimateTweetEmbedHeight(
    url: any,
    { width = 0, expanded = false }: { width?: number; expanded?: boolean } = {},
): number {
    return estimateTweetEmbedProjectionHeight(projectTweetEmbedGeometry(url), { width, expanded })
}

// The quote card's text slot (lines only; TwitterUrlEmbed pins .contentContainer to it). Zero
// when the quoted post has no text to show, in which case no slot renders.
export function estimateTweetQuoteTextHeight(
    embed: any,
    { width = 0, expanded = false }: { width?: number; expanded?: boolean } = {},
): number {
    if (!embed || embed.service_name !== 'twitter') return 0
    const text = expanded ? getTweetDisplayText(embed, true).text : collapsedTweetRenderText(embed)
    if (!text) return 0
    return (
        estimateTweetTextLines(text, {
            width,
            expanded,
            horizontalChrome: TWEET_QUOTE_TEXT_HORIZONTAL_CHROME_PX,
        }) * TWEET_TEXT_LINE_PX
    )
}

export type TweetCardGeometryProjection = {
    loading: boolean
    terminal: boolean
    hasDate: boolean
    collapsedText: string
    expandedText: string
    mediaTileHeights: number[]
    embed: TweetEmbedGeometryProjection
}

export function projectTweetCardGeometry(url: any): TweetCardGeometryProjection {
    const collapsedText = collapsedTweetRenderText(url)
    const expandedText = getTweetDisplayText(url, true).text
    const mediaItems = getTweetMediaGridItems(url)

    return {
        loading: tweetIsLoading(url),
        terminal: tweetIsTerminal(url),
        hasDate: !!url?.service_json?.created_at,
        collapsedText,
        expandedText,
        mediaTileHeights: mediaItems.map(item => tweetMediaBucketHeight(item.metadata)),
        embed: projectTweetEmbedGeometry(url),
    }
}

function estimateTweetProjectionChromeHeight(projection: TweetCardGeometryProjection): number {
    return TWEET_BASE_CHROME_PX + (projection.hasDate ? TWEET_DATE_PX : 0)
}

function estimateTweetProjectionMediaHeight(projection: TweetCardGeometryProjection): number {
    const gridHeight = estimateTweetGridHeight(projection.mediaTileHeights)
    return gridHeight ? gridHeight + TWEET_MEDIA_VMARGIN_PX : 0
}

export function estimateTweetCardProjectionHeight(
    projection: TweetCardGeometryProjection,
    { width = 0, expanded = false }: { width?: number; expanded?: boolean } = {},
): number {
    const embedHeight = estimateTweetEmbedProjectionHeight(projection.embed, { width, expanded })
    if (projection.loading) return TWEET_SKELETON_HEIGHT_PX
    if (projection.terminal) {
        // "This post is unavailable. Open it on X." — one line inside the text block.
        return (
            estimateTweetProjectionChromeHeight(projection) +
            TWEET_CONTENT_VMARGIN_PX +
            TWEET_TEXT_LINE_PX +
            embedHeight
        )
    }

    const text = expanded ? projection.expandedText : projection.collapsedText
    return (
        estimateTweetProjectionChromeHeight(projection) +
        estimateTweetTextBlockHeight(text, { width, expanded }) +
        estimateTweetProjectionMediaHeight(projection) +
        embedHeight
    )
}

// Content-derived reserved height for a locally-rendered tweet card (TwitterUrl.jsx):
//   - loading      → the fixed-shell skeleton height (constant; embeds only render once synced)
//   - unavailable  → chrome + a single line ("This post is unavailable…") + any embed card
//   - synced       → chrome + text block (wrapped at the row's width; none when the post has
//                    no text) + media (bucketed from the tweet's dims) + the url.embed
//                    quote/website card
// Because every term derives from the same payload fields the card renders from, the virtualizer
// estimate matches the rendered height and the card never collapses-then-grows.
// `opts` is optional so pre-existing 1-arg call sites keep their exact legacy behavior at the
// default 72 chars/line collapsed clamp.
export function estimateTweetCardHeight(
    url: any,
    { width = 0, expanded = false }: { width?: number; expanded?: boolean } = {},
): number {
    return estimateTweetCardProjectionHeight(projectTweetCardGeometry(url), { width, expanded })
}
